import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { fetchBillsIndex, fetchRecentVotes } from './fetch'
import type { BillsIndex, BillWithVotes, RecentVotes } from './models'

export type Loadable<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: string }

export interface AppData {
  recentVotes: Loadable<RecentVotes>
  triggerVotes: () => void
  billsIndex: Loadable<BillsIndex>
  triggerBillsIndex: () => void
  refetchBillsIndex: () => void
  cachedBill: (boletin: string) => BillWithVotes | undefined
  cacheBill: (bill: BillWithVotes) => void
}

const AppDataContext = createContext<AppData | null>(null)

export function useAppData(): AppData {
  const ctx = useContext(AppDataContext)
  if (!ctx) throw new Error('useAppData must be used within <DataProvider>')
  return ctx
}

/** Per-route bill selection, provided by ProyectoPage. */
export const BillContext = createContext<{ selectedBoletin: string | null }>({ selectedBoletin: null })
export function useBillContext() {
  return useContext(BillContext)
}

// DataProvider is mounted once at the app root for the app's lifetime, so
// in-flight fetch resolutions after unmount are not a concern (matching the Rust DataProvider).
export function DataProvider({ children }: { children: ReactNode }) {
  const [recentVotes, setRecentVotes] = useState<Loadable<RecentVotes>>({ status: 'idle' })
  const [billsIndex, setBillsIndex] = useState<Loadable<BillsIndex>>({ status: 'idle' })
  const [billCache, setBillCache] = useState<ReadonlyMap<string, BillWithVotes>>(new Map())
  const votesTriggered = useRef(false)
  const billsIndexTriggered = useRef(false)

  const seedCache = useCallback((bills: BillWithVotes[]) => {
    setBillCache((cache) => {
      const next = new Map(cache)
      for (const bill of bills) next.set(bill.boletin, bill)
      return next
    })
  }, [])

  const triggerVotes = useCallback(() => {
    if (votesTriggered.current) return
    votesTriggered.current = true
    setRecentVotes({ status: 'loading' })
    fetchRecentVotes()
      .then((data) => {
        setRecentVotes({ status: 'ready', data })
        seedCache(data.votes)
      })
      .catch((err: unknown) => setRecentVotes({ status: 'error', error: String(err) }))
  }, [seedCache])

  const loadBillsIndex = useCallback(() => {
    setBillsIndex({ status: 'loading' })
    fetchBillsIndex()
      .then((data) => setBillsIndex({ status: 'ready', data }))
      .catch((err: unknown) => setBillsIndex({ status: 'error', error: String(err) }))
  }, [])

  const triggerBillsIndex = useCallback(() => {
    if (billsIndexTriggered.current) return
    billsIndexTriggered.current = true
    loadBillsIndex()
  }, [loadBillsIndex])

  // refetchBillsIndex marks the index as triggered so a later triggerBillsIndex won't
  // duplicate the load. Used by the explorer error-retry path.
  const refetchBillsIndex = useCallback(() => {
    billsIndexTriggered.current = true
    loadBillsIndex()
  }, [loadBillsIndex])

  const cachedBill = useCallback((boletin: string) => billCache.get(boletin), [billCache])
  const cacheBill = useCallback((bill: BillWithVotes) => seedCache([bill]), [seedCache])

  const value = useMemo<AppData>(() => ({
    recentVotes, triggerVotes, billsIndex, triggerBillsIndex, refetchBillsIndex, cachedBill, cacheBill,
  }), [recentVotes, triggerVotes, billsIndex, triggerBillsIndex, refetchBillsIndex, cachedBill, cacheBill])

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
}
